import React, { Component } from 'react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import { navigateTo } from 'gatsby-link'
import BlockTitle from '@components/BlockTitle'
import ComboBox from '@components/Form/ComboBox'
import Markdown from '@components/Markdown'
import PageWrap from '@components/PageWrap'
import ProductImage from '@components/ProductImage'
import PpeItemList from '@components/PpeItemList'
import BookingForm from '@components/BookingForm'
import TechSpecTable from '@components/TechSpecTable'
import { productRoute } from '@constants/routes'
import { renderHireProductLink } from '@helpers/RenderHireProductLink'
import './Product.module.scss'
import { supportsWebp } from '@helpers/webp'

class Product extends Component {
  state = {
    format: 'jpg'
  }
  getOptions = R.map(
    (variant) => ({
      value: R.prop('slug', variant),
      text: R.path(['template', '0', 'shortName'], variant)
    })
  )

  getSpecs = R.pipe(
    R.defaultTo({}),
    R.mapObjIndexed(
      (value, key) => R.ifElse(
        (v) => Boolean(v),
        (v) => ({
          key,
          name: this.convertCamelCaseToTitle(key),
          value: v
        }),
        R.always(null)
      )(value)
    ),
    R.filter((item) => R.not(R.isNil(R.prop('value', item)))),
    R.values
  )

  getHeadings = R.ifElse(
    (variants) => R.gt(R.propOr(0, 'length', variants), 1),
    R.pipe(
      R.map(R.path(['template', '0', 'shortName'])),
      R.prepend('')
    ),
    R.always([''])
  )

  getLinks = R.map(R.prop('slug'))

  buildRows (variants) {
    const specArray = R.map(
      R.ifElse(
        R.pathOr(null, ['template', '0', 'spec']),
        (item) => this.getSpecs(R.path(['template', '0', 'spec'], item)),
        R.always([])
      ), variants)

    const specs = R.reduce(
      (prev, next) =>
        R.ifElse(
          ({ prev, next }) => R.gt(
            R.propOr(0, 'length', prev),
            R.propOr(0, 'length', next)
          ),
          R.prop('prev'),
          R.prop('next')
        )({ prev, next }),
      [],
      specArray
    )

    const getSpecValues = (variants, spec) => R.pipe(
      R.defaultTo([]),
      R.map(R.path(['template', '0', 'spec', spec.key]))
    )(variants)

    const getRows = (specs, variants) => R.map(
      (spec) => R.concat(
        [R.prop('name', spec)],
        getSpecValues(variants, spec)
      ), specs)

    return getRows(specs, variants)
  }

  onChange = (e) => {
    const { value } = e.target
    const { product } = this.props
    if (R.equals(value, 'Select Variant')) {
      return
    }

    navigateTo(productRoute(renderHireProductLink(value, R.prop('category', product))))
  }

  convertCamelCaseToTitle = (camelCase) => {
    return camelCase
      .replace(/([A-Z])/g, (match) => ` ${match}`)
      .replace(/^./, (match) => match.toUpperCase())
  }

  componentDidMount () {
    supportsWebp((isSupported) => {
      if (isSupported) {
        this.setState({format: 'webp'})
      }
    })
  }
  render () {
    const { product } = this.props
    const { format } = this.state
    if (R.or(R.not(product), R.isEmpty(product))) {
      return null
    }

    const productCode = R.propOr('', 'productCode', product)
    const title = R.prop('title', product)
    const shortName = R.prop('shortName', product)
    const slug = R.prop('slug', product)
    const description = R.prop('description', product)
    var imageSrc = R.path(['image', 'resize', 'src'], product)
    if (!imageSrc.includes('progressive')) {
      imageSrc = imageSrc + `&fm=${format}`
    }
    const ppe = R.prop('ppe', product)
    const spec = R.prop('spec', product)
    const variants = R.propOr([], 'variants', product)

    const self = {
      title: title,
      slug: slug,
      template: [
        {
          shortName: shortName,
          spec: spec
        }
      ]
    }

    const options = this.getOptions(variants)
    const specs = this.getSpecs(spec)
    const varsWIthSelf = R.prepend(self, variants)
    const headings = this.getHeadings(varsWIthSelf)
    const links = this.getLinks(varsWIthSelf)
    const rows = specs && this.buildRows(varsWIthSelf)
    const showVariants = R.gt(R.propOr(0, 'length', varsWIthSelf), 1)

    return (
      <PageWrap>
        {title && <BlockTitle title={title} DynamicTag='h1' />}
        <div styleName='product-code'>
          Product Code: {productCode}
        </div>
        <section styleName='cols'>
          {imageSrc && (
            <div styleName='imageWrapper'>
              <ProductImage src={imageSrc} alt={title} />
            </div>
          )}
          <div styleName='descriptionWrapper'>
            {showVariants &&
              <div styleName='variants-dropdown'>
                <h3 styleName='heading'>Product Variants</h3>
                <ComboBox
                  options={options}
                  change={this.onChange}
                  selectedValue={slug}
                  defaultValue='Select Variant'
                />
              </div>
            }
            {description && (
              <div styleName='description'>
                <h3 styleName='heading'>Features</h3>
                <Markdown content={description} />
              </div>
            )}
          </div>
        </section>
        <section>
          <BookingForm product={product} />
        </section>
        {R.and(spec, R.gt(rows.length, 0)) && (
          <section>
            <TechSpecTable headings={headings} links={links} rows={rows} slug={slug} />
          </section>
        )}
        {ppe &&
          <section>
            <PpeItemList items={ppe} />
          </section>
        }
      </PageWrap>
    )
  }
}

Product.propTypes = {
  product: PropTypes.object.isRequired
}

export default Product



// WEBPACK FOOTER //
// ./src/containers/Product/Product.js